/**
 * The art window the fingerprints were cut from, and the probe a client cuts it
 * with.
 *
 * ADR 0008 settled which rectangle the stored fingerprints describe: the
 * constant the pass-3 sweep fitted to them - 8% in from each side, 10.5% down,
 * 44% tall - and not the picture's own measured edges. The measurement places
 * the *picture* better; the constant is the better *cut*, because the printed
 * window barely moves between cards while Scryfall's crop has a shape of its
 * own, and a probe has to have that shape. The tool and the client therefore
 * have to cut the same rectangle, which is why it lives here rather than in
 * either of them.
 *
 * Two implementations of this exist on purpose and they must agree:
 *
 *  - `tools/catalog`'s `frameWindowHash` extracts the window with sharp, which
 *    is what every number in ADR 0006 and 0008 was measured through. Its
 *    real-image test compares this function against that one, card by card, so
 *    a divergence shows up as a failing assertion rather than as a scanner that
 *    quietly matches less often.
 *  - `artProbeHash` here is the same rectangle, the same 64x64 squash and the
 *    same fingerprint, in plain arithmetic: a browser has a canvas and no
 *    sharp, and a phone's device plugin will have neither.
 *
 * The probe is the only part of the cascade a *client* runs. Everything the
 * fingerprint is compared against - the index, the catalog, the fusion of the
 * two routes - happens where the data is.
 */
import { assertGreyImage, rectQuad, warpQuad } from './geometry.js';
import { PHASH_BITS, PHASH_SIZE, hashToHex, phashFromGrayLevels } from './phash.js';
/**
 * The window the fingerprints describe, in card fractions.
 *
 * `top` is where the picture starts and `height` how tall it is; the sides are
 * symmetric, so `inset` is both. These three numbers are the sweep's result and
 * a client must not invent its own: a probe cut anywhere else is compared
 * against fingerprints of this rectangle.
 */
export const INDEX_ART_WINDOW = { inset: 0.08, top: 0.105, height: 0.44 };
/** The window as the rectangle the frame is measured in. */
export function indexWindowRect(window = INDEX_ART_WINDOW) {
    return {
        x0: window.inset,
        y0: window.top,
        x1: 1 - window.inset,
        y1: window.top + window.height,
    };
}
/**
 * The art window of a card-relative frame, at `size x size` grey levels.
 *
 * The rectangle is clamped to the frame before it is cut, exactly as the sharp
 * path clamps it with `extract`: a window that runs off the edge of a frame
 * would otherwise be resampled from nothing.
 */
export function artProbeLevels(frame, options = {}) {
    assertGreyImage(frame, 'the frame to probe');
    const size = options.size ?? PHASH_SIZE;
    if (size < 1)
        throw new Error(`a probe cannot be ${size} pixels wide`);
    const rect = options.rect ?? indexWindowRect();
    const { width, height } = frame;
    const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
    const left = clamp(Math.round(rect.x0 * width), 0, width - 1);
    const top = clamp(Math.round(rect.y0 * height), 0, height - 1);
    const right = clamp(Math.round(rect.x1 * width), left + 1, width);
    const bottom = clamp(Math.round(rect.y1 * height), top + 1, height);
    const art = warpQuad(frame, rectQuad({ x0: left, y0: top, x1: right, y1: bottom }), size, size);
    return art.levels;
}
/** The fingerprint of a card frame's art window, as the index stores it. */
export function artProbeHash(frame, options = {}) {
    const size = options.size ?? PHASH_SIZE;
    const bits = options.bits ?? PHASH_BITS;
    return hashToHex(phashFromGrayLevels(artProbeLevels(frame, { ...options, size }), { size, bits }));
}
export function artProbe(frame, options = {}) {
    const rect = options.rect ?? indexWindowRect();
    return { frame, rect, hash: artProbeHash(frame, { ...options, rect }) };
}
//# sourceMappingURL=index-window.js.map